import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Play, Pause, Volume2, VolumeX, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function AIAvatarPresentation() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false); // Start with sound on
  const [currentStep, setCurrentStep] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const triedFallbackRef = useRef(false);
  const { toast } = useToast();

  const steps = [
    "Welcome to BizMatch!",
    "Step 1: List Your Business", 
    "Step 2: We Find Buyers",
    "Step 3: Connect & Close",
    "Step 4: Track Progress",
    "Ready to get started?"
  ];

  // Load video from Supabase Storage
  const loadVideo = async () => {
    console.log('Loading video...');
    setIsLoading(true);
    setHasError(false);
    
    try {
      // 1) Prefer your uploaded demo video
      const DEMO_VIDEO = '/videos/ai-avatar-demo.mp4';
      console.log('Trying demo video:', DEMO_VIDEO);
      try {
        const head = await fetch(DEMO_VIDEO, { method: 'HEAD' });
        if (head.ok) {
          console.log('Demo video found, using it');
          setVideoUrl(DEMO_VIDEO);
          triedFallbackRef.current = false;
          // toast({ title: 'Using your uploaded video', description: 'Playing the AI avatar demo.' });
          return;
        }
        console.log('Demo video not found, status:', head.status);
      } catch (err) {
        console.log('Demo video fetch failed:', err);
        // HEAD may fail in some environments; continue to storage lookup
      }

      // 2) Fallback: get the latest video file from Supabase Storage
      console.log('Checking Supabase storage for videos...');
      const { data: files, error } = await supabase.storage
        .from('ai-avatar-videos')
        .list('', { 
          limit: 1, 
          sortBy: { column: 'created_at', order: 'desc' } 
        });

      if (error) {
        console.error('Storage error:', error);
        throw new Error(`Storage error: ${error.message}`);
      }

      console.log('Storage files found:', files?.length || 0);
      if (!files || files.length === 0) {
        // Use local fallback demo video when none is found in storage
        console.log('No files in storage, using fallback video');
        setVideoUrl('/videos/ai-avatar-fallback.mp4');
        triedFallbackRef.current = true;
        toast({
          title: 'Using fallback video',
          description: 'No generated video found yet. Showing placeholder demo.',
        });
        return;
      }

      // Get public URL for the video
      console.log('Getting public URL for:', files[0].name);
      const { data: urlData } = supabase.storage
        .from('ai-avatar-videos')
        .getPublicUrl(files[0].name);

      if (!urlData.publicUrl) {
        throw new Error('Failed to get video URL');
      }

      console.log('Setting video URL from storage:', urlData.publicUrl);
      setVideoUrl(urlData.publicUrl);
      triedFallbackRef.current = false;
      
    } catch (error) {
      console.error('Error loading video:', error);
      // Fallback to local demo video on any load error
      setHasError(false);
      setVideoUrl('/videos/ai-avatar-fallback.mp4');
      triedFallbackRef.current = true;
      console.log('Using fallback due to error');
      toast({
        title: 'Showing fallback video',
        description: 'Using local demo while we fetch the AI video.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle play/pause
  const handlePlayPause = async () => {
    if (!videoRef.current || !videoUrl) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        // Ensure sound is on when user starts playback
        videoRef.current.muted = false;
        setIsMuted(false);
        await videoRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.error('Video play error:', error);
        toast({
          title: "Playback Error",
          description: "Unable to play video. Please try refreshing the page.",
          variant: "destructive",
        });
      }
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
    }
  };

  // Setup video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      const duration = video.duration;
      const stepDuration = duration / steps.length;
      
      // Animate through steps based on video duration
      steps.forEach((_, index) => {
        setTimeout(() => {
          setCurrentStep(index);
        }, stepDuration * index * 1000);
      });
    };

    const handleCanPlayThrough = () => {
      // Video is ready for manual playback - no autoplay
      setIsLoading(false);
      console.log('Video ready for manual playback');
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentStep(0);
    };

    const handleError = (e: Event) => {
      console.error('Video error:', e);
      console.error('Current video URL:', videoUrl);
      console.error('Tried fallback already:', triedFallbackRef.current);
      
      if (!triedFallbackRef.current) {
        // Try switching to local fallback once
        triedFallbackRef.current = true;
        setHasError(false);
        setVideoUrl('/videos/ai-avatar-fallback.mp4');
        console.log('Switching to fallback video');
        toast({
          title: 'Primary Source Failed',
          description: 'Reverting to fallback video.',
        });
        return;
      }
      setHasError(true);
      console.error('Both primary and fallback videos failed');
      toast({
        title: "Video Error",
        description: "Failed to load both primary and fallback videos. Please try refreshing the page.",
        variant: "destructive",
      });
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplaythrough', handleCanPlayThrough);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
    };
  }, [videoUrl]);

  // Load video on component mount
  useEffect(() => {
    loadVideo();
  }, []);

  // Cleanup video URL when component unmounts
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
      }
    };
  }, []);

  return (
    <Card className="mb-16 overflow-hidden bg-gradient-subtle border-border/50">
      <CardContent className="p-8">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-foreground mb-2">
            AI Avatar Demo
          </h3>
          <p className="text-muted-foreground">
            Watch our AI avatar explain how BizMatch works
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Video Avatar Area */}
          <div className="flex-1 flex justify-center">
            <div className="relative w-80 h-60 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/20 overflow-hidden shadow-elegant">
              {hasError ? (
                <div className="w-full h-full flex items-center justify-center text-center p-4">
                  <div>
                    <div className="w-12 h-12 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-2">
                      <span className="text-destructive">⚠</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Video unavailable. Please check admin panel to generate video.
                    </p>
                  </div>
                </div>
              ) : videoUrl ? (
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover rounded-lg"
                  src={videoUrl}
                  muted={isMuted}
                  playsInline
                  preload="metadata"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className={`w-20 h-20 rounded-full bg-primary/30 flex items-center justify-center ${
                    isLoading ? 'animate-bounce' : ''
                  }`}>
                    <div className="w-12 h-12 rounded-full bg-primary/50 flex items-center justify-center">
                      {isLoading ? (
                        <Loader2 className="w-6 h-6 text-primary-foreground animate-spin" />
                      ) : (
                        <Play className="w-6 h-6 text-primary-foreground" />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 text-center lg:text-left">
            {/* Current Step Display */}
            <div className="mb-6">
              <h4 className="text-xl font-semibold text-foreground mb-2 min-h-[28px]">
                {isLoading ? 'Loading AI video presentation...' : steps[currentStep]}
              </h4>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-gradient-primary h-2 rounded-full transition-all duration-500"
                  style={{ 
                    width: isLoading 
                      ? '0%' 
                      : `${((currentStep + 1) / steps.length) * 100}%` 
                  }}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex justify-center lg:justify-start gap-4">
              <Button
                onClick={handlePlayPause}
                disabled={isLoading || hasError || !videoUrl}
                variant="hero"
                size="lg"
                className="group"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
                <span className="ml-2">
                  {isLoading ? 'Loading...' : isPlaying ? 'Pause Demo' : 'Start Video Demo'}
                </span>
              </Button>

              {videoUrl && !hasError && (
                <>
                  <Button
                    onClick={toggleMute}
                    variant="outline"
                    size="lg"
                    className="animate-fade-in"
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </Button>
                  <Button
                    onClick={() => window.open(videoUrl, '_blank')}
                    variant="outline"
                    size="lg"
                    className="animate-fade-in"
                  >
                    Open in new tab
                  </Button>
                </>
              )}
            </div>

            <p className="text-sm text-muted-foreground mt-4">
              {isLoading 
                ? 'Loading your AI avatar presentation...' 
                : hasError 
                ? 'Video unavailable - please contact administrator'
                : isPlaying 
                ? 'Playing AI avatar presentation...' 
                : 'Click to watch how BizMatch works'
              }
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}